<template>
  <div class="main-layout">
    <ProductBuySection
      v-if="product"
      :product="product"
      :price="price"
      :loading="loading"
      :product-to-cart-format="productToCartFormat"
      :checkProductBuySectionATCInView="checkProductBuySectionATCInView"
      @disableAtcPageNav="disableAtcPageNav"
      :active-media="activeMedia"
      :configurations="configurations"
      @openGallery="openGallery"
      @updateActiveMedia="updateActiveMedia"
      @configuratorChanged="onConfiguratorChanged"
      class="product-page-buy-section"
      v-intersection="updateOverviewVisible"
    ></ProductBuySection>

    <transition name="product-gallery-fade">
      <suspense>
        <ProductGallery
          v-if="route?.query.gallery && mounted"
          @close="goBack()"
          :active-media="activeMedia"
          :product="product"
          @updateActiveMedia="updateActiveMedia"
          :notification="notification"
          @update:notification="notification = $event"
          @toggleSnackbar="toggleSnackbar"
        ></ProductGallery>
      </suspense>
    </transition>

    <div class="product-page-container">
      <Divider
        v-if="(!configurations || !configurations.length) && product && product.variation && product.variation.length > 1"
        class="delivery-message-top"
      />
    </div>

    <div class="product-page-container" v-if="product" v-view-time="onProductStoryViewed">
      <ContentfulRenderer
        :component="ProductContentfulSection"
        :content-type="`productPage`"
        :slug="`product-${product.hashKey}`"
        :is-required="false"
        :show-loader="false"
        :key="`product-${product.hashKey}`"
      />
    </div>

    <div class="product-page-container" id="details" ref="details" v-intersection="updateDetailsVisible">
      <ProductInformation :product="product" :last-image="lastImage" @openGallery="openDimensionGallery" @interaction="onAccordianClicked" />
    </div>
  </div>
</template>

<script>
import { Divider } from '@/pixel/index';
import { ref, onMounted, computed, defineAsyncComponent, watch, markRaw } from 'vue';
import Tracking from '@/components/commons/Tracking.js';
import ProductFavorite from '@/components/product/ProductFavorite.vue';
import ProductBuySection from '@/components/product/ProductBuySection.vue';
import ProductInformation from '@/components/product/ProductInformation.vue';
import LocationService from '@/services/LocationService';
import { useRouter, useRoute } from 'vue-router';
import Chat from '@/components/commons/Chat.js';
import useCommon from '@/compositions/useCommon';
import useLifecycleHook from '@/compositions/useLifecycleHook.js';
import Intersection from '@/components/commons/Intersection.js';
import ViewTime from '@/components/commons/ViewTime.js';
import ContentfulRenderer from '@/pages/Contentful/ContentfulRenderer.vue';

export default {
  name: 'MainLayout',
  props: {
    product: { type: Object, required: true },
    price: { type: Object, required: true },
    loading: { type: Boolean, default: false },
    loadConfiguration: { type: Function, required: true },
    lastImage: { type: Object, required: true },
    currentNavigationCategory: { type: Object, required: true },
    trackProductView: { type: Function, required: true },
    configurations: { type: Object, required: true },
    updateDetailsVisible: { type: Function, required: true },
    updateOverviewVisible: { type: Function, required: true },
    checkProductBuySectionATCInView: { type: Function, required: true },
    disableAtcPageNav: { type: Function, required: true },
  },

  components: {
    ContentfulRenderer,
    Divider,
    ProductBuySection,
    ProductFavorite,
    ProductGallery: defineAsyncComponent(() => import('@/components/product/ProductGallery.vue')),
    ProductInformation,
  },

  directives: { intersection: Intersection, viewTime: ViewTime },

  async setup(props) {
    const router = useRouter();
    const route = useRoute();
    const { mounted } = useLifecycleHook();
    const { initialized } = useCommon();

    const notification = ref(true);
    const activeMedia = ref(parseInt(route.query.gallery, 10) || 0);

    const lastMedia = computed(() => props.product.media.length - 1);

    const productToCartFormat = computed(() => [
      {
        title: props.product.title,
        id: props.product ? props.product.hashKey : null,
        quantity: props.product ? props.product.packagedItemQuantity : 1,
      },
    ]);

    function preventPinchZoom(event) {
      if (event.scale !== 1) {
        event.preventDefault();
      }
    }

    function updateGalleryDisplay(route) {
      const html = document.querySelector('html');
      if (html && route.query.hasOwnProperty('gallery')) {
        html.classList.add('overflow-hidden');
        Chat.hideChatWindow();
        document.addEventListener('touchmove', preventPinchZoom, false);
      } else {
        html.classList.remove('overflow-hidden');
        Chat.defaultChatWindow();
        document.removeEventListener('touchmove', preventPinchZoom);
      }
    }

    function trackImageView(index) {
      if (props.product && props.product) {
        Tracking.track('PDP View Product Image', {
          productId: props.product.hashKey,
          index,
        });
      }
    }

    async function onConfiguratorChanged(config) {
      await props.loadConfiguration(config);

      activeMedia.value = 0;
      await router.push({
        name: 'product',
        params: { id: props.product.hashKey, slug: props.product.slug, saveScrollPosition: true },
      });
      window.history.replaceState({ ...history.state }, '');
      props.trackProductView();
    }

    async function openGallery() {
      await router.push({
        name: 'product',
        params: { id: props.product.hashKey, slug: props.product.slug },
        query: { gallery: activeMedia.value },
      });
    }

    async function openDimensionGallery() {
      await router.push({
        name: 'product',
        params: { id: props.product.hashKey, slug: props.product.slug },
        query: { gallery: lastMedia.value },
      });
    }

    function updateActiveMedia(index) {
      activeMedia.value = index;
    }

    function onProductStoryViewed() {
      Tracking.track('PDP_View_Story');
    }

    function toggleSnackbar() {
      notification.value = false;
    }

    watch(activeMedia, async () => {
      trackImageView(activeMedia.value);
    });

    watch(
      () => route.query,
      () => {
        updateGalleryDisplay(route);
      },
    );

    onMounted(async () => {
      updateGalleryDisplay(route);
    });

    return {
      mounted,
      activeMedia,
      initialized,
      lastMedia,
      notification,
      onConfiguratorChanged,
      onProductStoryViewed,
      openDimensionGallery,
      openGallery,
      productToCartFormat,
      route,
      router,
      toggleSnackbar,
      trackImageView,
      updateActiveMedia,
      ProductContentfulSection: markRaw(defineAsyncComponent(() => import('@/components/product/ProductContentfulSection.vue'))),
    };
  },
  methods: {
    onSlideChange({ type, index }) {
      this.trackImageView(index);
    },

    onAccordianClicked(accordian) {
      Tracking.track('PDP_Mobile_Expand_Details', { section: accordian });
    },

    async goBack() {
      if (window.history.state.back && window.history.state.back === window.location.pathname) {
        window.history.back();
      } else {
        await this.router.push({
          name: 'product',
          params: { id: this.product.hashKey, slug: this.product.slug },
        });
      }
      this.toggleSnackbar();
    },
  },
};
</script>

<style lang="postcss" scoped>
@import '@/pixel/style/variables';
@import '@/pixel/style/mixins';

.product-page-buy-section {
  @media (min-width: $tablet-breakpoint) {
    margin-top: var(--space-4);
  }
}

.product-page-container {
  @media (max-width: calc($desktop-breakpoint - 1px)) {
    .gallery {
      padding: 0;
      width: 100%;
    }

    .mobile-only {
      display: none;
    }
  }

  @media (min-width: $large-desktop-breakpoint) {
    @mixin container 2560px;
  }

  position: relative;

  .row {
    &.large {
      margin-bottom: 20px;
    }
  }
}

.main-layout {
  width: 100%;

  .mobile-hidden {
    visibility: hidden;
  }

  .product-page-badge {
    z-index: 3;
    top: 20px;
    left: 20px;
  }

  .delivery-row {
    position: relative;
    margin-top: 12px;
  }

  .divider {
    &.delivery-message-top {
      margin: 12px 0;

      @media (max-width: calc($tablet-breakpoint - 1px)) {
        visibility: hidden;
      }
    }
  }
}
</style>
